import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import { useStorePath } from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { getWhitelabelConfig } from '@/lib/whitelabel';
import { buildStorefrontCategories } from '@/lib/storefrontCategories';
import { updateSocialMeta, absoluteStoreUrl } from '@/lib/socialMeta';
import { getProductImageUrl, resolveMediaUrl } from '@/lib/productMedia';
import BlockRenderer from '@/components/BlockRenderer';
import useTracking from '@/hooks/useTracking';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Store, Search, ShoppingCart, Package, BookOpen, Users, Heart, Grid, List, Loader2,
  MessageCircle, Facebook, Instagram, Twitter, Youtube, Linkedin, UserCircle, LogIn,
  Truck, Award, Headphones, ChevronLeft, ChevronRight, Star as StarIcon, Eye,
  MapPin, Phone, Mail, ArrowRight, Sparkles, Clock, Shield, Menu, X,
  Smartphone, Zap, Gamepad2, Wallet, CreditCard, CheckCircle2, AlertCircle,
  QrCode, Building, Landmark, Video, CalendarDays
} from 'lucide-react';

export default function PublicStorefront() {
  const subdomain = useStoreSubdomain();
  const storePath = useStorePath();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [extras, setExtras] = useState({});
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [bannerIdx, setBannerIdx] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [ppobProducts, setPpobProducts] = useState([]);
  const [ppobCategories, setPpobCategories] = useState([]);
  const [ppobCategory, setPpobCategory] = useState('');
  const [ppobActive, setPpobActive] = useState(false);
  const [ppobNumber, setPpobNumber] = useState('');
  const [ppobName, setPpobName] = useState('');
  const [ppobEmail, setPpobEmail] = useState('');
  const [ppobPhone, setPpobPhone] = useState('');
  const [ppobSelectedSku, setPpobSelectedSku] = useState('');
  const [ppobOrdering, setPpobOrdering] = useState(false);
  const [ppobResult, setPpobResult] = useState(null);
  const [ppobSearch, setPpobSearch] = useState('');
  const [ppobStep, setPpobStep] = useState(1);
  const [ppobPayment, setPpobPayment] = useState('bank_transfer');
  const [bundles, setBundles] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookingServices, setBookingServices] = useState([]);
  const [donationCampaigns, setDonationCampaigns] = useState([]);
  const [homepageBlocks, setHomepageBlocks] = useState(null);
  const [customPages, setCustomPages] = useState([]);
  const jsInjected = useRef(false);
  const { trackEvent } = useTracking(extras);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem(`affiliate_ref_${subdomain}`, refCode);
      api.post(`/store/${subdomain}/affiliate-click`, { code: refCode }).catch(() => {});
    }
    const fetchStore = async () => {
      try {
        const [storeRes, productsRes] = await Promise.all([
          api.get(`/store/${subdomain}`),
          api.get(`/store/${subdomain}/products`)
        ]);
        setStore(storeRes.data);
        const wl = getWhitelabelConfig(storeRes.data);
        const pageTitle = wl.brandName || storeRes.data.name;
        if (pageTitle) document.title = `${pageTitle} — Toko Online`;
        const seoDescription = storeRes.data.store_settings?.seo_description
          || storeRes.data.description
          || `Belanja online di ${pageTitle}`;
        updateSocialMeta({
          title: storeRes.data.store_settings?.seo_title || `${pageTitle} — Toko Online`,
          description: seoDescription,
          image: wl.logoUrl || storeRes.data.logo_url || '',
          url: absoluteStoreUrl('/'),
        });
        setProducts(productsRes.data.products || []);
        const storeExtras = storeRes.data.store_extras || {};
        setExtras(storeExtras);
        if (wl.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = wl.faviconUrl;
        }
        // Fetch homepage blocks
        try {
          const hpRes = await api.get(`/store/${subdomain}/page/home`);
          if (hpRes.data?.blocks?.length) setHomepageBlocks(hpRes.data.blocks);
        } catch {}
        // Fetch bundles
        try {
          const bundlesRes = await api.get(`/store/${subdomain}/bundles`);
          setBundles(bundlesRes.data.bundles || []);
        } catch {}
        // Fetch webinars
        try {
          const webinarsRes = await api.get(`/store/${subdomain}/webinars`);
          setWebinars(webinarsRes.data.webinars || []);
        } catch {}
        // Fetch events
        try {
          const eventsRes = await api.get(`/store/${subdomain}/events`);
          setEvents(eventsRes.data.events || []);
        } catch {}
        // Fetch booking services
        try {
          const bkRes = await api.get(`/store/${subdomain}/booking-services`);
          setBookingServices(bkRes.data.services || []);
        } catch {}
        // Fetch donation campaigns
        try {
          const donRes = await api.get(`/store/${subdomain}/donations`);
          setDonationCampaigns(donRes.data.campaigns || []);
        } catch {}
        // Fetch custom pages for nav
        try {
          const pagesRes = await api.get(`/store/${subdomain}/pages`);
          setCustomPages((pagesRes.data || []).filter(p => p.page_type !== 'home'));
        } catch {}
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
    const savedCart = localStorage.getItem(`cart_${subdomain}`);
    if (savedCart) setCart(JSON.parse(savedCart));
    const cToken = localStorage.getItem('customer_token');
    if (cToken) {
      api.get('/customer/wishlist/ids', { headers: { Authorization: `Bearer ${cToken}` } })
        .then(res => setWishlistIds(new Set(res.data.product_ids || [])))
        .catch(() => {});
    }
  }, [subdomain]);

  // Autofill PPOB customer data from login session
  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) {
      try {
        const u = JSON.parse(saved);
        if (u.role === 'customer') {
          setPpobName(n => n || u.name || '');
          setPpobEmail(e => e || u.email || '');
          if (localStorage.getItem('token')) {
            api.get('/customer/profile').then(res => {
              const c = res.data?.customer;
              if (c?.phone) setPpobPhone(p => p || c.phone);
            }).catch(() => {});
          }
        }
      } catch {}
    }
  }, []);

  // Fetch all PPOB products once when activated
  useEffect(() => {
    if (!ppobActive || !store) return;
    api.get(`/store/${subdomain}/ppob/products`)
      .then(res => {
        setPpobProducts(res.data.products || []);
        setPpobCategories(res.data.categories || []);
      })
      .catch(() => setPpobProducts([]));
  }, [ppobActive, subdomain]);

  const productTypes = buildStorefrontCategories(store, { webinars, events, bookingServices, donationCampaigns, products });
  const donationProducts = products.filter((p) => p.type === 'donation');
  const hasDonationContent = donationCampaigns.length > 0 || donationProducts.length > 0;

  useEffect(() => {
    if (!store) return;
    if (!productTypes.some((t) => t.id === activeType)) {
      setActiveType('all');
      setPpobActive(false);
    }
  }, [store, productTypes, activeType]);

  // Auto-rotate banner
  useEffect(() => {
    if (!store) return;
    const banners = store.banners || (store.banner_url ? [store.banner_url] : []);
    if (banners.length <= 1) return;
    const interval = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(interval);
  }, [store]);

  const addToCart = (product) => {
    const existing = cart.find(c => c.product_id === product.id);
    let newCart;
    if (existing) {
      newCart = cart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
    } else {
      newCart = [...cart, { product_id: product.id, name: product.name, price: product.sale_price || product.price, quantity: 1, image: getProductImageUrl(product) }];
    }
    setCart(newCart);
    localStorage.setItem(`cart_${subdomain}`, JSON.stringify(newCart));
    trackEvent('AddToCart', { id: product.id, name: product.name, price: product.sale_price || product.price });
  };

  const cartTotal = cart.reduce((a, c) => a + (c.price * c.quantity), 0);
  const cartCount = cart.reduce((a, c) => a + c.quantity, 0);

  const toggleWishlist = async (productId) => {
    const cToken = localStorage.getItem('customer_token');
    if (!cToken) return;
    const authHeader = { headers: { Authorization: `Bearer ${cToken}` } };
    const isWished = wishlistIds.has(productId);
    try {
      if (isWished) {
        await api.delete(`/customer/wishlist/${productId}`, authHeader);
        setWishlistIds(prev => { const s = new Set(prev); s.delete(productId); return s; });
      } else {
        await api.post(`/customer/wishlist/${productId}`, {}, authHeader);
        setWishlistIds(prev => new Set(prev).add(productId));
      }
    } catch {}
  };
  const customerLoggedIn = !!localStorage.getItem('customer_token');

  const filteredProducts = products.filter(p => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q
      || p.name.toLowerCase().includes(q)
      || (p.category || '').toLowerCase().includes(q)
      || (p.description || '').toLowerCase().includes(q);
    const matchCategory = !activeCategory || p.category === activeCategory;
    const matchType = activeType === 'all' || p.type === activeType;
    return matchSearch && matchCategory && matchType;
  });

  const ppobCategoryIcons = {
    'PULSA ALL OPERATOR': Smartphone, 'PAKET DATA': Smartphone, 'VOUCHER GAME': Gamepad2,
    'PULSA SMS TELEPHONE': Phone, 'PULSA TRANSFER': Smartphone, 'VOUCHER GOOGLE PLAY': CreditCard,
    'PUBG MOBILE UC': Gamepad2, 'VOUCHER WIFI.ID': Wallet, 'TOKEN LISTRIK': Zap,
    'E-TOLL': CreditCard, 'FREE FIRE DIAMOND': Gamepad2, 'MOBILE LEGEND': Gamepad2,
    'VOUCHER': CreditCard, 'E-MONEY': Wallet, 'MALAYSIA TOPUP': Smartphone,
    'SINGAPORE TOPUP': Smartphone, 'Pulsa BangJeff': Smartphone,
  };

  const ppobFilteredProducts = ppobProducts.filter(p => {
    const matchCat = !ppobCategory || p.category === ppobCategory;
    const q = ppobSearch.toLowerCase();
    const matchSearch = !q || (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const ppobSelectedProduct = ppobProducts.find(p => p.sku === ppobSelectedSku);

  const handlePpobOrder = async () => {
    if (!ppobSelectedSku || !ppobNumber || !ppobName || !ppobEmail) return;
    setPpobOrdering(true);
    setPpobResult(null);
    try {
      const res = await api.post(`/store/${subdomain}/ppob/order`, {
        sku: ppobSelectedSku,
        customer_number: ppobNumber,
        customer_name: ppobName,
        customer_email: ppobEmail,
        customer_phone: ppobPhone,
        payment_method: ppobPayment,
      });
      // Redirect to thank-you page
      const orderId = res.data.order?.id;
      const orderNumber = res.data.order_number || res.data.order?.order_number;
      navigate(storePath(`/thank-you?type=ppob&ppob_order=${orderId}&order=${orderNumber}`));
    } catch (e) {
      setPpobResult({ success: false, message: e.response?.data?.detail || 'Gagal membuat pesanan' });
    } finally {
      setPpobOrdering(false);
    }
  };

  const ppobReset = () => {
    setPpobStep(1);
    setPpobSelectedSku('');
    setPpobNumber('');
    setPpobName('');
    setPpobEmail('');
    setPpobPhone('');
    setPpobResult(null);
    setPpobSearch('');
    setPpobPayment('bank_transfer');
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <Loader2 size={36} className="animate-spin text-slate-400 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Memuat toko...</p>
      </div>
    </div>
  );

  if (!store) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <Store size={48} className="mx-auto text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Toko Tidak Ditemukan</h1>
        <p className="text-slate-500 mt-1">Subdomain "{subdomain}" tidak terdaftar</p>
      </div>
    </div>
  );

  const pc = store.primary_color || '#2D6CDF';
  const wl = getWhitelabelConfig(store);
  const displayName = wl.brandName;
  const displayLogo = wl.logoUrl;
  const banners = store.banners || (store.banner_url ? [store.banner_url] : []);
  const hasCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
  const newestProducts = [...products].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8);
  const bestSellers = [...products].sort((a, b) => (b.sold_count || 0) - (a.sold_count || 0)).slice(0, 8);
  const socialLinks = { facebook: extras.social_facebook, instagram: extras.social_instagram, twitter: extras.social_twitter, youtube: extras.social_youtube, linkedin: extras.social_linkedin };
  const hasSocial = Object.values(socialLinks).some(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid="public-storefront">
      {/* ===== TOP BAR ===== */}
      <div className="bg-slate-900 text-white text-xs py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {extras.wa_widget_number && <span className="flex items-center gap-1.5 text-slate-300"><Phone size={12} /> +62{extras.wa_widget_number.replace(/^0/, '')}</span>}
            {store.email && <span className="flex items-center gap-1.5 text-slate-300"><Mail size={12} /> {store.email}</span>}
            {!extras.wa_widget_number && !store.email && <span className="text-slate-400">Selamat datang di {displayName}</span>}
          </div>
          <div className="flex items-center gap-4">
            {hasSocial && (
              <div className="flex items-center gap-3">
                {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors"><Facebook size={13} /></a>}
                {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors"><Instagram size={13} /></a>}
                {socialLinks.twitter && <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors"><Twitter size={13} /></a>}
                {socialLinks.youtube && <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors"><Youtube size={13} /></a>}
                {socialLinks.linkedin && <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors"><Linkedin size={13} /></a>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== MAIN HEADER ===== */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            {/* Logo */}
            <Link to={`/store/${subdomain}`} className="flex items-center gap-3 flex-shrink-0">
              {displayLogo ? (
                <img src={displayLogo} alt={displayName} className="h-10 w-10 rounded-xl object-cover shadow-sm" />
              ) : (
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${pc}15` }}>
                  <Store size={22} style={{ color: pc }} />
                </div>
              )}
              <div className="hidden sm:block">
                <h1 className="font-bold text-lg text-slate-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>{displayName}</h1>
                {store.description && <p className="text-[11px] text-slate-500 line-clamp-1 max-w-[200px]">{store.description}</p>}
              </div>
            </Link>

            {/* Desktop Search */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cari produk di toko ini..."
                  className="pl-11 pr-4 h-11 rounded-full border-slate-300 bg-slate-50 focus:bg-white text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen(!searchOpen)} className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100">
                <Search size={20} />
              </button>
              <Link to={`/store/${subdomain}/blog`} className="hidden lg:flex">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 text-sm">Blog</Button>
              </Link>
              <Link to={`/store/${subdomain}/faq`} className="hidden lg:flex">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 text-sm">FAQ</Button>
              </Link>
              {customPages.slice(0, 3).map(pg => (
                <Link key={pg.slug} to={`/store/${subdomain}/page/${pg.slug}`} className="hidden lg:flex">
                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 text-sm">{pg.title}</Button>
                </Link>
              ))}
              <Link to={`/store/${subdomain}/login`}>
                <Button variant="ghost" size="sm" className="text-slate-600 gap-1.5 hidden sm:inline-flex">
                  <UserCircle size={18} /> Masuk
                </Button>
                <Button variant="ghost" size="icon" className="sm:hidden text-slate-600">
                  <UserCircle size={20} />
                </Button>
              </Link>
              <Link to={`/store/${subdomain}/checkout`}>
                <Button className="relative h-10 px-4 rounded-full shadow-sm" style={{ backgroundColor: pc }} data-testid="cart-btn">
                  <ShoppingCart size={18} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">{cartCount}</span>
                  )}
                  <span className="ml-2 hidden sm:inline text-sm font-medium">Rp {fmt(cartTotal)}</span>
                </Button>
              </Link>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100">
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          {searchOpen && (
            <div className="md:hidden pb-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk..." className="pl-9 rounded-full bg-slate-50" autoFocus />
              </div>
            </div>
          )}

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-slate-100 pt-3 space-y-1">
              <Link to={`/store/${subdomain}/blog`} className="block px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">Blog</Link>
              <Link to={`/store/${subdomain}/faq`} className="block px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">FAQ</Link>
              {customPages.map(pg => (
                <Link key={pg.slug} to={`/store/${subdomain}/page/${pg.slug}`} className="block px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">{pg.title}</Link>
              ))}
            </div>
          )}
        </div>

        {/* Category Tabs */}
        <div className="border-t border-slate-100 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2.5">
              {productTypes.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setActiveType(t.id); setActiveCategory(''); setPpobActive(t.id === 'ppob'); if (t.id !== 'ppob') setPpobResult(null); if (t.id !== 'all') { const target = ['webinar','event','booking','donation'].includes(t.id) ? `section-${t.id}` : 'filtered-products'; setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' }), 150); } }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-all ${
                    activeType === t.id
                      ? 'text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-200/60'
                  }`}
                  style={activeType === t.id ? { backgroundColor: pc } : {}}
                  data-testid={`filter-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ===== HOMEPAGE CONTENT (custom blocks or default) ===== */}
      {!search && !activeCategory && activeType === 'all' && (homepageBlocks ? (
        <BlockRenderer blocks={homepageBlocks} subdomain={subdomain} primaryColor={pc} />
      ) : (
        <>
          {/* ===== HERO BANNER / SLIDER ===== */}
          {banners.length > 0 ? (
            <section className="relative overflow-hidden bg-slate-100">
              <div className="relative aspect-[3/1] md:aspect-[3.5/1] max-h-[480px]">
                {banners.map((b, i) => (
                  <img
                    key={i}
                    src={b}
                    alt={`Banner ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === bannerIdx ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                {banners.length > 1 && (
                  <>
                    <button onClick={() => setBannerIdx(i => (i - 1 + banners.length) % banners.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-colors"><ChevronLeft size={22} /></button>
                    <button onClick={() => setBannerIdx(i => (i + 1) % banners.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-colors"><ChevronRight size={22} /></button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                      {banners.map((_, i) => (
                        <button key={i} onClick={() => setBannerIdx(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === bannerIdx ? 'bg-white scale-110' : 'bg-white/50'}`} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>
          ) : (
            <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${pc}12, ${pc}06)` }}>
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `radial-gradient(circle at 20% 50%, ${pc} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
              <div className="max-w-7xl mx-auto px-4 py-16 md:py-20 relative">
                <div className="max-w-2xl">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {store.name}
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed mb-6">{store.description || 'Temukan berbagai produk berkualitas terbaik dengan harga terjangkau'}</p>
                  <Button className="h-11 px-6 rounded-full shadow-sm" style={{ backgroundColor: pc }} onClick={() => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' })}>
                    Lihat Produk <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </section>
          )}

          {/* ===== TRUST BADGES ===== */}
          <section className="border-b border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                <div className="flex items-center gap-4 py-5 md:py-6 md:pr-8">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}10` }}>
                    <Truck size={24} style={{ color: pc }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900">Pengiriman Seluruh Indonesia</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Kurir lengkap & ongkir terjangkau</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-5 md:py-6 md:px-8">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-50">
                    <Award size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900">Kualitas Terjamin</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Produk original & berkualitas terbaik</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-5 md:py-6 md:pl-8">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-50">
                    <Headphones size={24} className="text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900">Customer Support</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Layanan pelanggan yang responsif</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== CATEGORY SHOWCASE ===== */}
          {hasCategories.length > 0 && (
            <section className="py-12 md:py-14 bg-slate-50">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kategori Produk</h2>
                    <p className="text-sm text-slate-500 mt-1">Jelajahi koleksi produk berdasarkan kategori</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {hasCategories.slice(0, 12).map((cat, i) => {
                    const catProducts = products.filter(p => p.category === cat);
                    const catImage = catProducts.map((p) => getProductImageUrl(p)).find(Boolean);
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setActiveCategory(cat);
                          setSearch('');
                          setActiveType('all');
                          setTimeout(() => document.getElementById('filtered-products')?.scrollIntoView({ behavior: 'smooth' }), 150);
                        }}
                        className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                      >
                        <div className="aspect-square bg-slate-100 overflow-hidden">
                          {catImage ? (
                            <img src={catImage} alt={cat} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${pc}08` }}>
                              <Package size={32} style={{ color: pc }} className="opacity-40" />
                            </div>
                          )}
                        </div>
                        <div className="p-3 text-center">
                          <h3 className="text-sm font-semibold text-slate-900 line-clamp-1">{cat}</h3>
                          <p className="text-xs text-slate-500 mt-0.5">{catProducts.length} produk</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ===== NEWEST PRODUCTS ===== */}
          {newestProducts.length > 0 && activeType !== 'ppob' && (
            <section className="py-12 md:py-14 bg-white" id="products-section">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full" style={{ backgroundColor: pc }} />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Produk Terbaru</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Koleksi produk terbaru dari {store.name}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full hidden md:flex" onClick={() => { setActiveType('all'); setSearch(''); setActiveCategory(''); }}>
                    Lihat Semua <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {newestProducts.map(p => (
                    <ProductCard key={p.id} product={p} subdomain={subdomain} primaryColor={pc} onAddToCart={addToCart} isWishlisted={wishlistIds.has(p.id)} onToggleWishlist={customerLoggedIn ? toggleWishlist : null} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ===== BEST SELLERS ===== */}
          {bestSellers.length > 0 && products.length > 4 && activeType !== 'ppob' && (
            <section className="py-12 md:py-14 bg-slate-50">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-amber-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Produk Terlaris</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Paling banyak diminati pelanggan</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full hidden md:flex" onClick={() => { setActiveType('all'); setSearch(''); setActiveCategory(''); }}>
                    Lihat Semua <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {bestSellers.map(p => (
                    <ProductCard key={p.id} product={p} subdomain={subdomain} primaryColor={pc} onAddToCart={addToCart} isWishlisted={wishlistIds.has(p.id)} onToggleWishlist={customerLoggedIn ? toggleWishlist : null} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ===== BUNDLE SECTION ===== */}
          {bundles.length > 0 && activeType !== 'ppob' && (
            <section className="py-12 md:py-14 bg-gradient-to-b from-white to-slate-50">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-purple-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Paket Bundling</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Hemat lebih banyak dengan paket bundle</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {bundles.map(b => {
                    const savings = b.original_total && b.final_price ? Math.round((1 - b.final_price / b.original_total) * 100) : 0;
                    return (
                      <Link key={b.id} to={`/store/${subdomain}/bundle/${b.id}`} className="group">
                        <Card className="overflow-hidden border border-slate-200 hover:shadow-lg transition-all hover:border-purple-200">
                          <div className="relative">
                            {b.images?.[0] ? (
                              <img src={b.images[0]} alt={b.name} className="w-full h-48 object-cover" />
                            ) : (
                              <div className="w-full h-48 bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center">
                                <Package size={40} className="text-purple-300" />
                              </div>
                            )}
                            {savings > 0 && (
                              <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0 text-xs font-bold px-2 py-1">
                                Hemat {savings}%
                              </Badge>
                            )}
                            <Badge className="absolute top-3 left-3 bg-purple-600 text-white border-0 text-xs font-medium px-2 py-1">
                              Bundle
                            </Badge>
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors line-clamp-1">{b.name}</h3>
                            {b.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{b.description}</p>}
                            <div className="flex items-center gap-2 mt-3">
                              {b.original_total > b.final_price && (
                                <span className="text-sm text-slate-400 line-through">Rp {b.original_total.toLocaleString('id-ID')}</span>
                              )}
                              <span className="text-lg font-bold" style={{ color: pc }}>Rp {b.final_price?.toLocaleString('id-ID')}</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">{b.items?.length || 0} produk dalam paket</p>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ===== DONATION CAMPAIGNS (homepage preview) ===== */}
          {donationCampaigns.length > 0 && activeType !== 'ppob' && activeType !== 'donation' && activeType !== 'webinar' && activeType !== 'event' && (
            <section className="py-12 md:py-14 bg-gradient-to-b from-pink-50/50 to-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-pink-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Donasi & Galang Dana</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Bantu campaign yang sedang berjalan</p>
                    </div>
                  </div>
                  {donationCampaigns.length > 3 && (
                    <Button variant="outline" size="sm" className="rounded-full hidden md:flex" onClick={() => setActiveType('donation')}>
                      Lihat Semua <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {donationCampaigns.slice(0, 6).map((c) => (
                    <DonationCampaignCard key={c.id} campaign={c} to={storePath(`/donate/${c.id}`)} primaryColor={pc} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ===== WEBINAR SECTION ===== */}
          {webinars.length > 0 && activeType !== 'ppob' && activeType !== 'webinar' && activeType !== 'event' && (
            <section className="py-12 md:py-14 bg-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-indigo-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Webinar</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Ikuti webinar & tingkatkan skill Anda</p>
                    </div>
                  </div>
                  {webinars.length > 3 && (
                    <Button variant="outline" size="sm" className="rounded-full hidden md:flex" onClick={() => setActiveType('webinar')}>
                      Lihat Semua <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {webinars.slice(0, 6).map(w => (
                    <Link key={w.id} to={`/store/${subdomain}/webinar/${w.id}`} className="group">
                      <Card className="overflow-hidden border border-slate-200 hover:shadow-lg transition-all hover:border-indigo-200">
                        <div className="relative">
                          {w.thumbnail ? (
                            <img src={w.thumbnail} alt={w.title} className="w-full h-48 object-cover" />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center">
                              <Video size={40} className="text-indigo-300" />
                            </div>
                          )}
                          <Badge className={`absolute top-3 left-3 border-0 text-xs font-medium px-2 py-1 ${
                            w.status === 'upcoming' ? 'bg-blue-600 text-white' : w.status === 'live' ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'
                          }`}>
                            {w.status === 'upcoming' ? 'Akan Datang' : w.status === 'live' ? 'LIVE' : 'Rekaman'}
                          </Badge>
                          {w.price === 0 && (
                            <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0 text-xs font-bold px-2 py-1">GRATIS</Badge>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">{w.title}</h3>
                          {w.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{w.description}</p>}
                          <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                            {w.speaker_name && <span className="flex items-center gap-1"><UserCircle size={14} /> {w.speaker_name}</span>}
                            {w.scheduled_at && <span className="flex items-center gap-1"><CalendarDays size={14} /> {new Date(w.scheduled_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-lg font-bold" style={{ color: pc }}>
                              {w.price > 0 ? `Rp ${w.price.toLocaleString('id-ID')}` : 'Gratis'}
                            </span>
                            {w.max_participants > 0 && (
                              <span className="text-xs text-slate-400">
                                <Users size={12} className="inline mr-1" />{w.max_participants - (w.registration_count || 0)} slot
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ===== EVENT SECTION ===== */}
          {events.length > 0 && activeType !== 'ppob' && activeType !== 'webinar' && activeType !== 'event' && (
            <section className="py-12 md:py-14 bg-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-violet-500" />
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Event & Ticketing</h2>
                      <p className="text-sm text-slate-500 mt-0.5">Event offline & dapatkan tiketnya</p>
                    </div>
                  </div>
                  {events.length > 3 && (
                    <Button variant="outline" size="sm" className="rounded-full hidden md:flex" onClick={() => setActiveType('event')}>
                      Lihat Semua <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {events.slice(0, 6).map(ev => (
                    <Link key={ev.id} to={`/store/${subdomain}/event/${ev.id}`} className="group">
                      <Card className="overflow-hidden border border-slate-200 hover:shadow-lg transition-all hover:border-violet-200">
                        <div className="relative">
                          {ev.thumbnail ? (
                            <img src={ev.thumbnail} alt={ev.title} className="w-full h-48 object-cover" />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center">
                              <CalendarDays size={40} className="text-violet-300" />
                            </div>
                          )}
                          <Badge className={`absolute top-3 left-3 border-0 text-xs font-medium px-2 py-1 ${
                            ev.status === 'upcoming' ? 'bg-blue-600 text-white' : ev.status === 'ongoing' ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'
                          }`}>
                            {ev.status === 'upcoming' ? 'Akan Datang' : ev.status === 'ongoing' ? 'Berlangsung' : ev.status === 'completed' ? 'Selesai' : ev.status}
                          </Badge>
                          {ev.min_price === 0 && (
                            <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0 text-xs font-bold px-2 py-1">GRATIS</Badge>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-slate-900 group-hover:text-violet-600 transition-colors line-clamp-1">{ev.title}</h3>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            {ev.venue_name && <span className="flex items-center gap-1"><MapPin size={14} /> {ev.venue_name}</span>}
                            {ev.start_date && <span className="flex items-center gap-1"><CalendarDays size={14} /> {new Date(ev.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-lg font-bold" style={{ color: pc }}>
                              {ev.min_price > 0 ? `Rp ${ev.min_price.toLocaleString('id-ID')}` : 'Gratis'}
                            </span>
                            {ev.total_capacity > 0 && (
                              <span className="text-xs text-slate-400">
                                <Users size={12} className="inline mr-1" />{ev.total_capacity - (ev.total_sold || 0)} slot
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      ))}

      {/* ===== BOOKING SERVICES SECTION ===== */}
      {bookingServices.length > 0 && activeType !== 'ppob' && activeType !== 'webinar' && activeType !== 'event' && activeType !== 'booking' && (
        <section className="py-12 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold" style={{ color: store?.store_settings?.primary_color || '#1e40af' }}>📅 Booking & Reservasi</h2>
              {bookingServices.length > 3 && (
                <button onClick={() => setActiveType('booking')} className="text-sm font-medium" style={{ color: store?.store_settings?.primary_color || '#2563eb' }}>Lihat Semua →</button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookingServices.slice(0, 6).map(svc => (
                <Link key={svc.id} to={`/store/${subdomain}/booking/${svc.id}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                    <div className="relative h-44 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                      {svc.images?.[0] ? <img src={svc.images[0]} alt={svc.name} className="w-full h-full object-cover" /> : <span className="text-4xl">🚗</span>}
                      <span className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                        {svc.booking_mode === 'daily' ? 'Harian' : svc.booking_mode === 'hourly' ? 'Per Jam' : svc.booking_mode === 'session' ? 'Per Sesi' : 'Per Unit'}
                      </span>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg line-clamp-1">{svc.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                        {svc.location && <span>📍 {svc.location}</span>}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-bold" style={{ color: store?.store_settings?.primary_color || '#2563eb' }}>
                          Rp {(svc.price || 0).toLocaleString('id-ID')}<span className="text-xs font-normal text-slate-400 ml-1">{svc.price_unit}</span>
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== BOOKING LIST (when booking filter active) ===== */}
      {activeType === 'booking' && (
        <section id="section-booking" className="py-12 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-xl md:text-2xl font-bold mb-6" style={{ color: store?.store_settings?.primary_color || '#1e40af' }}>📅 Semua Booking & Reservasi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookingServices.map(svc => (
                <Link key={svc.id} to={`/store/${subdomain}/booking/${svc.id}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full">
                    <div className="relative h-44 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                      {svc.images?.[0] ? <img src={svc.images[0]} alt={svc.name} className="w-full h-full object-cover" /> : <span className="text-4xl">🚗</span>}
                      <span className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                        {svc.booking_mode === 'daily' ? 'Harian' : svc.booking_mode === 'hourly' ? 'Per Jam' : svc.booking_mode === 'session' ? 'Per Sesi' : 'Per Unit'}
                      </span>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg line-clamp-1">{svc.name}</h3>
                      {svc.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{svc.description}</p>}
                      <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                        {svc.location && <span>📍 {svc.location}</span>}
                        {svc.operating_hours && <span>🕐 {svc.operating_hours}</span>}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-bold" style={{ color: store?.store_settings?.primary_color || '#2563eb' }}>
                          Rp {(svc.price || 0).toLocaleString('id-ID')}<span className="text-xs font-normal text-slate-400 ml-1">{svc.price_unit}</span>
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== WEBINAR LIST (when webinar filter active) ===== */}
      {activeType === 'webinar' && (
        <section id="section-webinar" className="py-12 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Semua Webinar</h2>
                <p className="text-sm text-slate-500 mt-0.5">{webinars.length} webinar tersedia</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setActiveType('all')} className="text-slate-500">Reset Filter</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {webinars.map(w => (
                <Link key={w.id} to={`/store/${subdomain}/webinar/${w.id}`} className="group">
                  <Card className="overflow-hidden border border-slate-200 hover:shadow-lg transition-all hover:border-indigo-200">
                    <div className="relative">
                      {w.thumbnail ? (
                        <img src={w.thumbnail} alt={w.title} className="w-full h-48 object-cover" />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center">
                          <Video size={40} className="text-indigo-300" />
                        </div>
                      )}
                      <Badge className={`absolute top-3 left-3 border-0 text-xs font-medium px-2 py-1 ${
                        w.status === 'upcoming' ? 'bg-blue-600 text-white' : w.status === 'live' ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'
                      }`}>
                        {w.status === 'upcoming' ? 'Akan Datang' : w.status === 'live' ? 'LIVE' : 'Rekaman'}
                      </Badge>
                      {w.price === 0 && (
                        <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0 text-xs font-bold px-2 py-1">GRATIS</Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">{w.title}</h3>
                      {w.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{w.description}</p>}
                      <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                        {w.speaker_name && <span className="flex items-center gap-1"><UserCircle size={14} /> {w.speaker_name}</span>}
                        {w.scheduled_at && <span className="flex items-center gap-1"><CalendarDays size={14} /> {new Date(w.scheduled_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-lg font-bold" style={{ color: pc }}>
                          {w.price > 0 ? `Rp ${w.price.toLocaleString('id-ID')}` : 'Gratis'}
                        </span>
                        {w.max_participants > 0 && (
                          <span className="text-xs text-slate-400">
                            <Users size={12} className="inline mr-1" />{w.max_participants - (w.registration_count || 0)} slot
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== EVENT LIST (when event filter active) ===== */}
      {activeType === 'event' && (
        <section id="section-event" className="py-12 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Semua Event</h2>
                <p className="text-sm text-slate-500 mt-0.5">{events.length} event tersedia</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setActiveType('all')} className="text-slate-500">Reset Filter</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map(ev => (
                <Link key={ev.id} to={`/store/${subdomain}/event/${ev.id}`} className="group">
                  <Card className="overflow-hidden border border-slate-200 hover:shadow-lg transition-all hover:border-violet-200">
                    <div className="relative">
                      {ev.thumbnail ? (
                        <img src={ev.thumbnail} alt={ev.title} className="w-full h-48 object-cover" />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-violet-50 to-purple-100 flex items-center justify-center">
                          <CalendarDays size={40} className="text-violet-300" />
                        </div>
                      )}
                      <Badge className={`absolute top-3 left-3 border-0 text-xs font-medium px-2 py-1 ${
                        ev.status === 'upcoming' ? 'bg-blue-600 text-white' : ev.status === 'ongoing' ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'
                      }`}>
                        {ev.status === 'upcoming' ? 'Akan Datang' : ev.status === 'ongoing' ? 'Berlangsung' : ev.status === 'completed' ? 'Selesai' : ev.status}
                      </Badge>
                      {ev.min_price === 0 && (
                        <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0 text-xs font-bold px-2 py-1">GRATIS</Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-slate-900 group-hover:text-violet-600 transition-colors line-clamp-1">{ev.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                        {ev.venue_name && <span className="flex items-center gap-1"><MapPin size={14} /> {ev.venue_name}</span>}
                        {ev.start_date && <span className="flex items-center gap-1"><CalendarDays size={14} /> {new Date(ev.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-lg font-bold" style={{ color: pc }}>
                          {ev.min_price > 0 ? `Rp ${ev.min_price.toLocaleString('id-ID')}` : 'Gratis'}
                        </span>
                        {ev.total_capacity > 0 && (
                          <span className="text-xs text-slate-400">
                            <Users size={12} className="inline mr-1" />{ev.total_capacity - (ev.total_sold || 0)} slot
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== DONATION LIST (when donation filter active) ===== */}
      {activeType === 'donation' && (
        <section id="section-donation" className="py-12 md:py-14 bg-gradient-to-b from-pink-50/50 to-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Semua Campaign Donasi</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {donationCampaigns.length} campaign{donationProducts.length > 0 ? ` · ${donationProducts.length} produk donasi` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setActiveType('all')} className="text-slate-500">Reset Filter</Button>
            </div>
            {donationCampaigns.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                {donationCampaigns.map((c) => (
                  <DonationCampaignCard key={c.id} campaign={c} to={storePath(`/donate/${c.id}`)} primaryColor={pc} />
                ))}
              </div>
            )}
            {donationProducts.length > 0 && (
              <div>
                {donationCampaigns.length > 0 && (
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Produk Donasi</h3>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {donationProducts.map((p) => (
                    <ProductCard key={p.id} product={p} subdomain={subdomain} primaryColor={pc} onAddToCart={addToCart} isWishlisted={wishlistIds.has(p.id)} onToggleWishlist={customerLoggedIn ? toggleWishlist : null} />
                  ))}
                </div>
              </div>
            )}
            {!hasDonationContent && (
              <div className="py-20 text-center">
                <Heart size={48} className="mx-auto text-pink-200 mb-4" />
                <p className="text-slate-500 font-medium">Belum ada campaign donasi</p>
                <p className="text-sm text-slate-400 mt-1">Campaign aktif dari dashboard tenant akan muncul di sini</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== ALL PRODUCTS (if search or filter active) ===== */}
      {(search || activeCategory || (activeType !== 'all' && activeType !== 'ppob' && activeType !== 'webinar' && activeType !== 'event' && activeType !== 'booking' && activeType !== 'donation')) && (
        <section className="py-12 md:py-14 bg-white" id="filtered-products">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {search
                    ? `Hasil pencarian "${search}"`
                    : activeCategory
                      ? `Kategori: ${activeCategory}`
                      : `Semua ${productTypes.find(t => t.id === activeType)?.label || ''}`}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">{filteredProducts.length} produk ditemukan</p>
              </div>
              {(search || activeCategory || activeType !== 'all') && (
                <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setActiveCategory(''); setActiveType('all'); }} className="text-slate-500">
                  Reset Filter
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredProducts.map(p => (
                <ProductCard key={p.id} product={p} subdomain={subdomain} primaryColor={pc} onAddToCart={addToCart} isWishlisted={wishlistIds.has(p.id)} onToggleWishlist={customerLoggedIn ? toggleWishlist : null} />
              ))}
            </div>
            {!filteredProducts.length && (
              <div className="py-20 text-center">
                <Package size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-medium">Tidak ada produk ditemukan</p>
                <p className="text-sm text-slate-400 mt-1">Coba kata kunci atau kategori lain</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== PPOB SECTION ===== */}
      {activeType === 'ppob' && (
        <section className="py-8 md:py-12 bg-gradient-to-b from-slate-50 to-white" id="products-section">
          <div className="max-w-3xl mx-auto px-4">

            {/* Step Indicator - compact */}
            <div className="flex items-center gap-1 mb-6">
              {[
                { n: 1, label: 'Produk' },
                { n: 2, label: 'Data' },
                { n: 3, label: 'Bayar' },
                { n: 4, label: 'Konfirmasi' },
              ].map((s, i) => (
                <div key={s.n} className="flex items-center gap-1 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                    ppobStep >= s.n ? 'text-white' : 'bg-slate-200 text-slate-400'
                  }`} style={ppobStep >= s.n ? { backgroundColor: pc } : {}}>
                    {ppobStep > s.n ? <CheckCircle2 size={14} /> : s.n}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${ppobStep >= s.n ? 'text-slate-700' : 'text-slate-400'}`}>{s.label}</span>
                  {i < 3 && <div className={`flex-1 h-0.5 rounded ${ppobStep > s.n ? '' : 'bg-slate-200'}`} style={ppobStep > s.n ? { backgroundColor: pc } : {}} />}
                </div>
              ))}
            </div>

            {/* Step 1: Browse & Select Product */}
            {ppobStep === 1 && (
              <div>
                {/* Phase A: Category Selection (when no category chosen) */}
                {!ppobCategory ? (
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-1">Pilih Kategori</h2>
                    <p className="text-sm text-slate-500 mb-5">Mau beli apa hari ini?</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {ppobCategories.map(cat => {
                        const Icon = ppobCategoryIcons[cat] || CreditCard;
                        const count = ppobProducts.filter(p => p.category === cat).length;
                        // Short label
                        const shortLabel = cat.replace('VOUCHER ', '').replace('PULSA ', '').replace('ALL OPERATOR', 'All Opr').replace('SMS TELEPHONE', 'SMS/Tel').replace('PAKET ', '');
                        return (
                          <button
                            key={cat}
                            onClick={() => { setPpobCategory(cat); setPpobSearch(''); }}
                            className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-slate-100 bg-white hover:border-slate-300 hover:shadow-md transition-all group"
                          >
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100 group-hover:scale-110 transition-transform" style={{ color: pc }}>
                              <Icon size={24} />
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-semibold text-slate-700 leading-tight line-clamp-2">{shortLabel}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{count} produk</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Quick search below categories */}
                    <div className="mt-6">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Atau cari langsung produk..."
                          value={ppobSearch}
                          onChange={(e) => { setPpobSearch(e.target.value); }}
                          className="pl-9 rounded-xl h-11"
                        />
                      </div>
                      {ppobSearch && (
                        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
                          {ppobFilteredProducts.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">Tidak ditemukan</p>
                          ) : (
                            ppobFilteredProducts.slice(0, 20).map(p => {
                              const Icon = ppobCategoryIcons[p.category] || CreditCard;
                              const isSelected = ppobSelectedSku === p.sku;
                              return (
                                <button
                                  key={p.sku}
                                  onClick={() => { setPpobSelectedSku(p.sku); setPpobStep(2); }}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                    isSelected ? 'shadow-md border-2' : 'border-slate-200 hover:bg-slate-50'
                                  }`}
                                  style={isSelected ? { borderColor: pc, backgroundColor: `${pc}08` } : {}}
                                >
                                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100">
                                    <Icon size={16} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-slate-900 truncate">{p.name}</p>
                                    <p className="text-[11px] text-slate-400">{p.brand} · {p.category}</p>
                                  </div>
                                  <span className="font-bold text-sm flex-shrink-0" style={{ color: pc }}>Rp {fmt(p.selling_price)}</span>
                                </button>
                              );
                            })
                          )}
                          {ppobFilteredProducts.length > 20 && (
                            <p className="text-xs text-slate-400 text-center py-2">+{ppobFilteredProducts.length - 20} lainnya, ketik lebih spesifik...</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Phase B: Product list within chosen category */
                  <div>
                    {/* Back + category header */}
                    <button
                      onClick={() => { setPpobCategory(''); setPpobSelectedSku(''); setPpobSearch(''); }}
                      className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-3 transition-colors"
                    >
                      <ChevronLeft size={16} /> Semua Kategori
                    </button>
                    <div className="flex items-center gap-3 mb-4">
                      {(() => { const Icon = ppobCategoryIcons[ppobCategory] || CreditCard; return (
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pc}15`, color: pc }}>
                          <Icon size={22} />
                        </div>
                      ); })()}
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-slate-900">{ppobCategory}</h2>
                        <p className="text-xs text-slate-500">{ppobFilteredProducts.length} produk tersedia</p>
                      </div>
                    </div>

                    {/* Search within category */}
                    <div className="relative mb-4">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder={`Cari di ${ppobCategory.toLowerCase()}...`}
                        value={ppobSearch}
                        onChange={(e) => setPpobSearch(e.target.value)}
                        className="pl-9 rounded-xl h-10 text-sm"
                      />
                    </div>

                    {/* Brand quick filters */}
                    {(() => {
                      const brands = [...new Set(ppobFilteredProducts.map(p => p.brand).filter(Boolean))];
                      if (brands.length <= 1) return null;
                      return (
                        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide pb-1">
                          {brands.map(b => (
                            <button
                              key={b}
                              onClick={() => setPpobSearch(ppobSearch === b ? '' : b)}
                              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-all ${
                                ppobSearch === b ? 'text-white border-transparent' : 'text-slate-600 border-slate-200 hover:bg-slate-50'
                              }`}
                              style={ppobSearch === b ? { backgroundColor: pc } : {}}
                            >
                              {b}
                            </button>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Product list - compact */}
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                      {ppobFilteredProducts.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-sm text-slate-400">Produk tidak ditemukan</p>
                        </div>
                      ) : (
                        ppobFilteredProducts.map(p => {
                          const isSelected = ppobSelectedSku === p.sku;
                          return (
                            <button
                              key={p.sku}
                              onClick={() => setPpobSelectedSku(isSelected ? '' : p.sku)}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                                isSelected ? 'shadow-md' : 'border-slate-100 hover:border-slate-200 bg-white'
                              }`}
                              style={isSelected ? { borderColor: pc, backgroundColor: `${pc}06` } : {}}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? '' : 'border-slate-300'}`} style={isSelected ? { borderColor: pc } : {}}>
                                {isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pc }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-slate-800 truncate">{p.name}</p>
                                {p.brand && <p className="text-[11px] text-slate-400">{p.brand}</p>}
                              </div>
                              <span className={`font-bold text-sm flex-shrink-0 ${isSelected ? '' : 'text-slate-600'}`} style={isSelected ? { color: pc } : {}}>
                                Rp {fmt(p.selling_price)}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Sticky bottom: selected product + next */}
                    {ppobSelectedSku && ppobSelectedProduct && (
                      <div className="sticky bottom-0 mt-4 p-3 bg-white/95 backdrop-blur rounded-2xl border-2 shadow-lg flex items-center gap-3" style={{ borderColor: `${pc}40` }}>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">{ppobSelectedProduct.name}</p>
                          <p className="font-bold text-sm" style={{ color: pc }}>Rp {fmt(ppobSelectedProduct.selling_price)}</p>
                        </div>
                        <Button onClick={() => setPpobStep(2)} className="rounded-full px-5 h-10 font-semibold text-sm" style={{ backgroundColor: pc }}>
                          Lanjut <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Customer Details */}
            {ppobStep === 2 && (
              <div>
                {/* Selected product chip */}
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 mb-5 shadow-sm">
                  {(() => { const Icon = ppobCategoryIcons[ppobSelectedProduct?.category] || CreditCard; return <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}12`, color: pc }}><Icon size={18} /></div>; })()}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{ppobSelectedProduct?.name}</p>
                    <p className="text-xs text-slate-400">{ppobSelectedProduct?.brand}</p>
                  </div>
                  <span className="font-bold text-sm" style={{ color: pc }}>Rp {fmt(ppobSelectedProduct?.selling_price)}</span>
                  <button onClick={() => { setPpobStep(1); }} className="text-slate-400 hover:text-slate-600 ml-1"><X size={16} /></button>
                </div>

                <h3 className="font-bold text-lg text-slate-900 mb-4">Data Pelanggan</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nomor Tujuan <span className="text-red-500">*</span></label>
                    <Input value={ppobNumber} onChange={e => setPpobNumber(e.target.value)} placeholder="Contoh: 08123456789" className="rounded-xl h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nama Lengkap <span className="text-red-500">*</span></label>
                    <Input value={ppobName} onChange={e => setPpobName(e.target.value)} placeholder="Nama lengkap" className="rounded-xl h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email <span className="text-red-500">*</span></label>
                    <Input type="email" value={ppobEmail} onChange={e => setPpobEmail(e.target.value)} placeholder="email@contoh.com" className="rounded-xl h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">No. HP <span className="text-slate-400">(opsional)</span></label>
                    <Input value={ppobPhone} onChange={e => setPpobPhone(e.target.value)} placeholder="08xxxxxxxxxx" className="rounded-xl h-11" />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setPpobStep(1)} className="rounded-full px-5 h-11">
                    <ChevronLeft size={16} className="mr-1" /> Kembali
                  </Button>
                  <Button
                    onClick={() => setPpobStep(3)}
                    disabled={!ppobNumber || !ppobName || !ppobEmail}
                    className="flex-1 rounded-full h-11 font-semibold"
                    style={{ backgroundColor: pc }}
                  >
                    Lanjutkan <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Payment Method */}
            {ppobStep === 3 && (
              <div>
                {/* Selected product chip */}
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 mb-5 shadow-sm">
                  {(() => { const Icon = ppobCategoryIcons[ppobSelectedProduct?.category] || CreditCard; return <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}12`, color: pc }}><Icon size={18} /></div>; })()}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{ppobSelectedProduct?.name}</p>
                    <p className="text-xs text-slate-400">No: {ppobNumber}</p>
                  </div>
                  <span className="font-bold text-sm" style={{ color: pc }}>Rp {fmt(ppobSelectedProduct?.selling_price)}</span>
                </div>

                <h3 className="font-bold text-lg text-slate-900 mb-4">Metode Pembayaran</h3>
                <div className="space-y-2">
                  {[
                    { value: 'qris', label: 'QRIS', desc: 'Scan QR untuk bayar', icon: QrCode },
                    { value: 'va', label: 'Virtual Account', desc: 'Transfer via VA', icon: Building },
                    { value: 'ewallet', label: 'E-Wallet', desc: 'OVO, GoPay, Dana', icon: Wallet },
                    { value: 'bank_transfer', label: 'Transfer Bank', desc: 'Transfer manual ke rekening', icon: Landmark },
                  ].map(pm => {
                    const Icon = pm.icon;
                    const isSelected = ppobPayment === pm.value;
                    return (
                      <button
                        key={pm.value}
                        onClick={() => setPpobPayment(pm.value)}
                        className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                          isSelected ? 'shadow-sm' : 'border-slate-100 hover:border-slate-200 bg-white'
                        }`}
                        style={isSelected ? { borderColor: pc, backgroundColor: `${pc}06` } : {}}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isSelected ? '' : 'bg-slate-100'}`} style={isSelected ? { backgroundColor: `${pc}15`, color: pc } : {}}>
                          <Icon size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-slate-900">{pm.label}</p>
                          <p className="text-xs text-slate-500">{pm.desc}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? '' : 'border-slate-300'}`} style={isSelected ? { borderColor: pc } : {}}>
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pc }} />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setPpobStep(2)} className="rounded-full px-5 h-11">
                    <ChevronLeft size={16} className="mr-1" /> Kembali
                  </Button>
                  <Button
                    onClick={() => setPpobStep(4)}
                    className="flex-1 rounded-full h-11 font-semibold"
                    style={{ backgroundColor: pc }}
                  >
                    Lanjutkan <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {ppobStep === 4 && (
              <div>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="p-5 border-b border-slate-100">
                    <h3 className="font-bold text-lg text-slate-900">Konfirmasi Pesanan</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Produk</span>
                      <span className="text-sm font-medium text-slate-900 text-right max-w-[60%] truncate">{ppobSelectedProduct?.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Kategori</span>
                      <span className="text-sm text-slate-700">{ppobSelectedProduct?.category}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Nomor Tujuan</span>
                      <span className="text-sm font-medium text-slate-900">{ppobNumber}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Nama</span>
                      <span className="text-sm text-slate-700">{ppobName}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Email</span>
                      <span className="text-sm text-slate-700">{ppobEmail}</span>
                    </div>
                    {ppobPhone && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-sm text-slate-500">No. HP</span>
                        <span className="text-sm text-slate-700">{ppobPhone}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">Pembayaran</span>
                      <span className="text-sm font-medium text-slate-900">
                        {{ qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', bank_transfer: 'Transfer Bank' }[ppobPayment]}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 bg-slate-50 flex items-center justify-between">
                    <span className="font-semibold text-slate-900">Total Bayar</span>
                    <span className="font-bold text-xl" style={{ color: pc }}>Rp {fmt(ppobSelectedProduct?.selling_price)}</span>
                  </div>
                </div>

                {ppobResult?.success === false && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-sm text-red-700 mt-4">
                    <AlertCircle size={16} /> {ppobResult.message}
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setPpobStep(3)} className="rounded-full px-5 h-11">
                    <ChevronLeft size={16} className="mr-1" /> Kembali
                  </Button>
                  <Button
                    onClick={handlePpobOrder}
                    disabled={ppobOrdering}
                    className="flex-1 rounded-full h-11 font-semibold"
                    style={{ backgroundColor: pc }}
                  >
                    {ppobOrdering ? <Loader2 size={16} className="animate-spin mr-2" /> : <CreditCard size={16} className="mr-2" />}
                    {ppobOrdering ? 'Memproses...' : 'Buat Pesanan'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== ABOUT / STORE INFO ===== */}
      {store.description && (
        <section className="py-14 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-5 overflow-hidden shadow-sm border border-slate-200">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${pc}10` }}><Store size={28} style={{ color: pc }} /></div>
                )}
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>Tentang {store.name}</h2>
              <p className="text-slate-600 leading-relaxed">{store.description}</p>
            </div>
          </div>
        </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4">
          {/* Main footer */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 py-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {displayLogo ? (
                  <img src={displayLogo} alt={displayName} className="h-10 w-10 rounded-xl object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pc}30` }}><Store size={22} className="text-white" /></div>
                )}
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{displayName}</span>
              </div>
              <p className="text-sm leading-relaxed mb-4">{store.description || 'Toko online terpercaya dengan produk berkualitas terbaik'}</p>
              {hasSocial && (
                <div className="flex items-center gap-3">
                  {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"><Facebook size={16} /></a>}
                  {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"><Instagram size={16} /></a>}
                  {socialLinks.twitter && <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"><Twitter size={16} /></a>}
                  {socialLinks.youtube && <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"><Youtube size={16} /></a>}
                  {socialLinks.linkedin && <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"><Linkedin size={16} /></a>}
                </div>
              )}
            </div>
            {/* Navigation */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Navigasi</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to={`/store/${subdomain}`} className="hover:text-white transition-colors">Beranda</Link></li>
                <li><button onClick={() => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">Produk</button></li>
                <li><Link to={`/store/${subdomain}/blog`} className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to={`/store/${subdomain}/faq`} className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to={`/store/${subdomain}/login`} className="hover:text-white transition-colors">Masuk / Daftar</Link></li>
                {customPages.map(pg => (
                  <li key={pg.slug}><Link to={`/store/${subdomain}/page/${pg.slug}`} className="hover:text-white transition-colors">{pg.title}</Link></li>
                ))}
              </ul>
            </div>
            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Hubungi Kami</h4>
              <ul className="space-y-3 text-sm">
                {extras.wa_widget_number && (
                  <li className="flex items-start gap-2.5">
                    <Phone size={16} className="mt-0.5 flex-shrink-0" />
                    <span>+62{extras.wa_widget_number.replace(/^0/, '')}</span>
                  </li>
                )}
                {store.email && (
                  <li className="flex items-start gap-2.5">
                    <Mail size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{store.email}</span>
                  </li>
                )}
                {store.address && (
                  <li className="flex items-start gap-2.5">
                    <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{store.address}</span>
                  </li>
                )}
                {!extras.wa_widget_number && !store.email && !store.address && (
                  <li className="text-slate-500">Hubungi melalui WhatsApp</li>
                )}
              </ul>
            </div>
          </div>
          {/* Copyright */}
          <div className={`border-t border-slate-800 py-5 flex flex-col md:flex-row items-center gap-3 ${wl.hidePoweredBy ? 'justify-center' : 'justify-between'}`}>
            <p className="text-xs text-slate-500">
              {wl.footerText || `\u00A9 ${new Date().getFullYear()} ${displayName}. All Rights Reserved.`}
            </p>
            {!wl.hidePoweredBy && (
              <p className="text-xs text-slate-600">Powered by <span style={{ color: pc }}>Tokomoe</span></p>
            )}
          </div>
        </div>
      </footer>

      {/* ===== FLOATING WHATSAPP ===== */}
      {extras.wa_widget_enabled && extras.wa_widget_number && (
        <a
          href={`https://wa.me/62${extras.wa_widget_number.replace(/^0/, '')}?text=${encodeURIComponent(extras.wa_widget_message || 'Halo, saya tertarik dengan produk di toko Anda')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-600 text-white pl-4 pr-5 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105"
          title="Chat WhatsApp"
        >
          <MessageCircle size={22} />
          <span className="text-sm font-semibold hidden sm:inline">Chat Kami</span>
        </a>
      )}

      {/* Custom CSS (whitelabel) */}
      {wl.customCss && (
        <style>{wl.customCss}</style>
      )}

      {/* Custom JS injection */}
      {extras.custom_js && !jsInjected.current && (() => {
        jsInjected.current = true;
        const script = document.createElement('script');
        script.textContent = extras.custom_js;
        document.body.appendChild(script);
        return null;
      })()}
    </div>
  );
}

/* ===== DONATION CAMPAIGN CARD ===== */
function DonationCampaignCard({ campaign, to, primaryColor }) {
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const pct = campaign.target_amount > 0
    ? Math.min(100, Math.round(((campaign.total_raised || 0) / campaign.target_amount) * 100))
    : 0;

  return (
    <Link to={to} className="group">
      <Card className="overflow-hidden border border-pink-200 hover:shadow-lg transition-all hover:border-pink-300 h-full">
        <div className="relative h-44 bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center overflow-hidden">
          {campaign.thumbnail ? (
            <img src={resolveMediaUrl(campaign.thumbnail)} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <Heart size={40} className="text-pink-300" />
          )}
          <Badge className="absolute top-3 left-3 bg-pink-500 text-white border-0 text-xs font-bold px-2 py-1 gap-1">
            <Heart size={10} /> Donasi
          </Badge>
        </div>
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-900 group-hover:text-pink-600 transition-colors line-clamp-2">{campaign.title}</h3>
          {campaign.description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{campaign.description}</p>
          )}
          {campaign.target_amount > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-bold" style={{ color: primaryColor }}>Rp {fmt(campaign.total_raised)}</span>
                <span className="text-slate-500">/ Rp {fmt(campaign.target_amount)}</span>
              </div>
              <div className="h-2 rounded-full bg-pink-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">{pct}% tercapai · {campaign.donor_count || 0} donatur</p>
            </div>
          )}
          <Button className="w-full mt-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-full h-9 text-xs font-semibold">
            <Heart size={14} className="mr-1.5" /> Donasi Sekarang
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}

/* ===== PRODUCT CARD ===== */
function ProductCard({ product, subdomain, primaryColor, onAddToCart, isWishlisted, onToggleWishlist }) {
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
  const isDonation = product.type === 'donation';

  return (
    <div className={`group bg-white rounded-2xl border overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${isDonation ? 'border-pink-200' : 'border-slate-200'}`} data-testid={`product-card-${product.id}`}>
      {/* Image */}
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {getProductImageUrl(product) ? (
          <img src={getProductImageUrl(product)} alt={product.name} className="w-full h-full object-cover [@media(hover:hover)_and_(pointer:fine)]:group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDonation ? 'bg-gradient-to-br from-pink-100 to-rose-100' : ''}`}>
            {isDonation ? <Heart size={36} className="text-pink-300" /> : <Package size={36} className="text-slate-300" />}
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {isDonation ? (
            <Badge className="bg-pink-500 text-white text-[10px] font-bold px-2 py-0.5 shadow-sm gap-1">
              <Heart size={10} /> Donasi
            </Badge>
          ) : (
            hasDiscount && (
              <Badge className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 shadow-sm">-{discountPercent}%</Badge>
            )
          )}
        </div>
        {/* Wishlist */}
        {onToggleWishlist && !isDonation && (
          <button onClick={() => onToggleWishlist(product.id)} className="absolute top-2.5 right-2.5 p-2 rounded-full bg-white/90 hover:bg-white shadow-sm transition-all hover:scale-110" data-testid={`wishlist-${product.id}`}>
            <Heart size={16} className={isWishlisted ? 'fill-red-500 text-red-500' : 'text-slate-400'} />
          </button>
        )}
        {/* Quick action overlay — desktop / mouse only (avoids stuck hover on touch) */}
        <div className="absolute inset-x-0 bottom-0 p-3 translate-y-full transition-transform duration-300 [@media(hover:hover)_and_(pointer:fine)]:group-hover:translate-y-0">
          {isDonation ? (
            <Link to={`/store/${subdomain}/product/${product.id}`}>
              <Button className="w-full h-9 text-xs font-semibold rounded-full shadow-lg bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600">
                <Heart size={14} className="mr-1.5" /> Donasi Sekarang
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() => onAddToCart(product)}
              className="w-full h-9 text-xs font-semibold rounded-full shadow-lg"
              style={{ backgroundColor: primaryColor }}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart size={14} className="mr-1.5" /> Tambah ke Keranjang
            </Button>
          )}
        </div>
      </div>
      {/* Info */}
      <div className="p-3.5">
        <Link to={`/store/${subdomain}/product/${product.id}`}>
          <h3 className="font-medium text-sm text-slate-900 line-clamp-2 hover:underline leading-snug min-h-[2.5rem]">{product.name}</h3>
        </Link>
        {isDonation ? (
          <div className="mt-2.5">
            {product.donation_target > 0 && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Heart size={11} className="text-pink-400" /> Target: <span className="font-semibold text-pink-600">Rp {fmt(product.donation_target)}</span>
              </p>
            )}
            <p className="text-[11px] text-slate-400 mt-1">Mulai dari Rp 1.000</p>
            <Link to={`/store/${subdomain}/product/${product.id}`} className="[@media(hover:hover)_and_(pointer:fine)]:hidden block mt-3">
              <Button className="w-full h-9 text-xs font-semibold rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600">
                <Heart size={14} className="mr-1.5" /> Donasi Sekarang
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-2.5 flex items-baseline gap-2">
              <span className="font-bold text-base" style={{ color: primaryColor }}>Rp {fmt(product.sale_price || product.price)}</span>
              {hasDiscount && <span className="text-xs text-slate-400 line-through">Rp {fmt(product.price)}</span>}
            </div>
            {product.sold_count > 0 && (
              <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1"><StarIcon size={11} className="fill-amber-400 text-amber-400" /> Terjual {product.sold_count}</p>
            )}
            <Button
              onClick={() => onAddToCart(product)}
              className="[@media(hover:hover)_and_(pointer:fine)]:hidden w-full h-9 mt-3 text-xs font-semibold rounded-full shadow-sm"
              style={{ backgroundColor: primaryColor }}
              data-testid={`add-to-cart-mobile-${product.id}`}
            >
              <ShoppingCart size={14} className="mr-1.5" /> Tambah ke Keranjang
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
